import { Component, computed, input, output } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwProgress } from '../../ui/progress/uw-progress';
import type { QueueItem } from '../../../core/models/queue.model';
import { UwMoneyPipe, UwCompactMoneyPipe, UwInitialsPipe } from '../../pipes/format.pipes';

@Component({
  selector: 'uw-queue-item',
  standalone: true,
  imports: [UwIcon, UwBadge, UwProgress, UwMoneyPipe, UwCompactMoneyPipe, UwInitialsPipe],
  template: `
    <article
      role="button"
      tabindex="0"
      [attr.aria-label]="'Open account ' + item().accountName"
      [class]="classes()"
      (click)="selected.emit(item())"
      (keydown.enter)="selected.emit(item())"
      (keydown.space)="selected.emit(item())"
      (focus)="focused.emit(item().id)"
    >
      <div class="flex w-full items-start gap-3">
        <span class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-[11px] font-bold text-primary">
          {{ item().accountName | uwInitials }}
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="truncate text-[13px] font-semibold text-primary">{{ item().accountName }}</p>
              <p class="mt-0.5 truncate text-[11.5px] text-text-muted">
                {{ item().product }} · Broker: {{ item().broker }}
              </p>
            </div>
            <div class="shrink-0 text-right">
              <p class="typo-mono text-[12.5px] font-semibold text-primary">{{ item().premium | uwMoney }}</p>
              <p class="typo-mono text-[10.5px] text-text-muted">TIV {{ item().tiv | uwCompactMoney }}</p>
            </div>
          </div>

          <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <uw-badge [tone]="item().statusTone">{{ item().statusLabel }}</uw-badge>
            @if (item().sla) {
              <span class="flex items-center gap-1 text-[10.5px] font-semibold" [class.text-danger]="item().slaTone === 'critical'"
                [class.text-warning]="item().slaTone === 'warn'" [class.text-text-muted]="!item().slaTone">
                <uw-icon name="clock" [size]="11" />
                SLA {{ item().sla }}
              </span>
            }
            @if (item().confidence > 0 && item().category === 'straight-through') {
              <span class="flex items-center gap-1.5">
                <uw-progress [value]="item().confidence" tone="success" [label]="'AI match confidence'" />
                <span class="typo-mono text-[10px] font-semibold text-success">{{ item().confidence }}%</span>
              </span>
            }
          </div>

          @if (item().reason) {
            <p class="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-text-muted">
              <uw-icon name="bot" [size]="11" class="mt-0.5 shrink-0 text-ai" />
              {{ item().reason }}
            </p>
          }
        </div>
      </div>
    </article>
  `,
})
export class UwQueueItem {
  readonly item = input.required<QueueItem>();
  readonly active = input(false);

  readonly selected = output<QueueItem>();
  readonly focused = output<string>();

  protected readonly classes = computed(() =>
    [
      'group w-full cursor-pointer rounded-lg border bg-surface-elevated p-3 text-left transition-all',
      this.active()
        ? 'border-ai shadow-md'
        : 'border-line hover:-translate-y-px hover:border-ai/60 hover:shadow-md',
    ].join(' '),
  );
}
